import { MuiBox, MuiTypography } from '@/features/shared/material-ui';
import { identity, pickBy } from 'lodash';
import { getOr } from 'lodash/fp';
import React, { memo, useCallback, useMemo } from 'react';
import { useSDLContent } from '../hooks/useSDLContent';
import { sdlContentSpecParser } from '../utils/SDLContent.utils';
import { deviceTypeResolveMap, resolveDefaultInfoSpec } from './Basic.constants';

const BasicInformation = ({ content, labels, deviceType }: any) => {
  const { parseContentRawProp } = useSDLContent(content);

  const { firmwareInformation, basicInformation, additionalInformation } = useMemo(
    () => ({
      basicInformation: parseContentRawProp('basic_information'),
      additionalInformation: parseContentRawProp('additional_information'),
      firmwareInformation: parseContentRawProp('firmware_information'),
    }),
    [parseContentRawProp],
  );

  const io = useMemo(() => content[0]?.io?.raw, [content]);

  const toCamels = (stringVar: string) => {
    const first = stringVar.substring(1, 0);
    const second = stringVar.substring(1).toLowerCase();
    const camels = first + second;
    const compress = `${camels}StreamsPerChannel`;
    return compress;
  };

  const rawCompressionTypes = content[0]?.compression_types?.raw;
  const compressionTypes = useMemo(
    () => (Array.isArray(rawCompressionTypes) ? rawCompressionTypes : [rawCompressionTypes]),
    [rawCompressionTypes],
  );

  const compressionTypesMaxStreams = useCallback(() => {
    let endResult: any = {};
    const result: any = [];
    const streams: any = [];

    compressionTypes?.map((types: any) => {
      if (types && types.length < 1) {
        return streams.push(types);
      }
      if (types && types.length >= 1) {
        return streams.push(types.split(','));
      }
      return undefined;
    });

    streams?.flat()?.map((stream: any) => {
      const eachStream = Object.entries(additionalInformation.CompressionTypes).filter(
        (type: any) => type[0] === stream.trim(),
      );
      eachStream?.map((e: any) => {
        const val = Object.entries(e[1]).filter((x: any) => x[0] === toCamels(e[0]));
        return result.push(`${e[0]}(${val[0][1]})`);
      });
      return result;
    });

    endResult = result;

    return endResult.length ? endResult : ['N/A'];
  }, [additionalInformation.CompressionTypes, compressionTypes]);

  const initialRelease = useMemo(
    () =>
      pickBy(
        {
          omnicast: firmwareInformation?.Omnicast?.OmnInitialRelease,
          security_center: firmwareInformation?.SecurityCenter?.SecurityCenterInitialRelease,
        },
        identity,
      ),
    [
      firmwareInformation?.Omnicast?.OmnInitialRelease,
      firmwareInformation?.SecurityCenter?.SecurityCenterInitialRelease,
    ],
  );
  const maxStreams = useMemo(() => compressionTypesMaxStreams(), [compressionTypesMaxStreams]);
  const stratocastModel = useMemo(
    () => [<a href={additionalInformation?.ModelLink}>{basicInformation?.ModelName}</a>],
    [additionalInformation?.ModelLink, basicInformation?.ModelName],
  );

  const basicInfo = useMemo(() => {
    const deviceTypeInfoSpec = getOr(resolveDefaultInfoSpec, deviceType, deviceTypeResolveMap);
    return sdlContentSpecParser(
      {
        additionalInformation,
        basicInformation,
        initialRelease,
        io,
        maxStreams,
        stratocastModel,
      },
      deviceTypeInfoSpec,
      'N/A',
    );
  }, [
    additionalInformation,
    basicInformation,
    deviceType,
    initialRelease,
    io,
    maxStreams,
    stratocastModel,
  ]);

  const isObj = (obj: any) => {
    return Object.prototype.toString.call(obj) === '[object Object]';
  };
  const isArr = (arr: any) => {
    return Array.isArray(arr);
  };

  return (
    <MuiBox display="flex" flexDirection="column" flexWrap="wrap" className="basic-detail-data">
      {!!basicInformation &&
        Object.keys(basicInfo).map((key) => (
          <div key={key + Math.random()}>
            <MuiTypography variant="overline" component="h6" role="heading" data-testid="sdl-title">
              {labels[key]}
            </MuiTypography>
            <ul>
              <li key={key + Math.random()}>
                {isObj((basicInfo as any)[key]) &&
                  Object.keys((basicInfo as any)[key]).map((key2: any) => (
                    <div key={key + Math.random()}>
                      {labels[key2]}: {(basicInfo as any)[key][key2]}
                    </div>
                  ))}
                {isArr((basicInfo as any)[key]) &&
                  (basicInfo as any)[key].map((keyA: any) => (
                    <div key={key + Math.random()}>{keyA}</div>
                  ))}
                {!isObj((basicInfo as any)[key]) &&
                  !isArr((basicInfo as any)[key]) &&
                  (basicInfo as any)[key]}
              </li>
            </ul>
          </div>
        ))}
    </MuiBox>
  );
};

export default memo(BasicInformation);
